import {useMemo, useState} from "react";
import {Alert, Segmented, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {ExportButtons, FontAwesomeIcon} from "~/components";
import {useGetCompetitorCoverageQuery} from "~/reduxs/api/competitorApiSlice";
import ConnectGscCta from "~/features/KeywordResearch/components/ConnectGscCta";
import style from "../style/Competitor.module.scss";

const cn = className.bind(style);

/** Nhãn + màu verdict của BE (defend/advantage/neutral). */
const VERDICT_META = {
	defend:    {label: 'Cần phòng thủ', color: 'red',   hint: 'Bạn ngoài trang 1 mà đối thủ đã có nội dung phủ — ưu tiên cải thiện.'},
	advantage: {label: 'Đang lợi thế',  color: 'green', hint: 'Bạn top 5 và chưa đối thủ nào phủ — giữ vững & mở rộng.'},
	neutral:   {label: 'Trung lập',     color: 'default', hint: ''},
};

/**
 * Tab "Độ phủ từ khoá" — ma trận top query GSC của dự án × đối thủ nào có nội dung phủ
 * từ đó (đối chiếu cụm từ các trang đối thủ ĐÃ phân tích ở tab Content Gap — thuần DB,
 * không tốn lượt). Heuristic: khớp cụm 1-3 từ + fallback mọi-từ-đều-có; chỉ phản ánh
 * các trang đối thủ đã phân tích, KHÔNG phải toàn site đối thủ.
 */
function CompetitorCoverage({projectId}) {

	const {data, isFetching} = useGetCompetitorCoverageQuery({projectId});
	const [verdictFilter, setVerdictFilter] = useState('all');

	const competitors = useMemo(() => data?.competitors || [], [data]);
	const rows = useMemo(() => {
		const all = data?.rows || [];
		return verdictFilter === 'all' ? all : all.filter((r) => r.verdict === verdictFilter);
	}, [data, verdictFilter]);

	if (!isFetching && data && !data.connected) {
		return <ConnectGscCta projectId={projectId}
			message="Độ phủ từ khoá dùng top truy vấn thật từ Google Search Console — dự án này chưa kết nối." />;
	}

	const anyAnalyzed = competitors.some((c) => c.analyzed);

	const columns = [
		{
			title: 'Từ khoá (top impressions)', dataIndex: 'keyword', key: 'kw', ellipsis: true,
			render: (v, r) => (
				<span>
					{v}{' '}
					{r.verdict !== 'neutral' && (
						<Tooltip title={VERDICT_META[r.verdict].hint}>
							<Tag color={VERDICT_META[r.verdict].color}>{VERDICT_META[r.verdict].label}</Tag>
						</Tooltip>
					)}
				</span>
			),
		},
		// Cột phụ — ẩn dưới 768px cho gọn mobile.
		{title: 'Impressions', dataIndex: 'impressions', key: 'impr', width: 110, align: 'center', responsive: ['md'],
			sorter: (a, b) => a.impressions - b.impressions,
			render: (v) => v.toLocaleString('vi-VN')},
		{title: 'Vị trí của bạn', dataIndex: 'position', key: 'pos', width: 110, align: 'center',
			sorter: (a, b) => a.position - b.position,
			render: (v) => v > 0 ? v : '—'},
		...competitors.map((c) => ({
			title: (
				<Tooltip title={c.analyzed ? `${c.pages} trang đã phân tích` : 'Chưa phân tích trang nào — chạy Content Gap trước'}>
					<span>{c.name}{!c.analyzed && ' *'}</span>
				</Tooltip>
			),
			key: `c-${c.id}`,
			width: 120,
			align: 'center',
			render: (_, r) => {
				if (!c.analyzed) return <span className={cn('hint')}>?</span>;
				return r.covered?.[c.id]
					? <FontAwesomeIcon icon="fa-solid fa-circle-check" style={{color: '#16a34a'}} />
					: <span className={cn('hint')}>—</span>;
			},
		})),
		{title: 'Đối thủ phủ', dataIndex: 'covered_count', key: 'cc', width: 100, align: 'center',
			sorter: (a, b) => a.covered_count - b.covered_count,
			render: (v) => <Tag color={v > 0 ? 'volcano' : 'default'}>{v}</Tag>},
	];

	const buildExcel = () => [{
		name: 'Độ phủ từ khoá',
		aoa: [
			['Từ khoá', 'Clicks', 'Impressions', 'Vị trí của bạn', ...competitors.map((c) => c.domain), 'Số đối thủ phủ', 'Đánh giá'],
			...(data?.rows || []).map((r) => [
				r.keyword, r.clicks, r.impressions, r.position || '',
				...competitors.map((c) => (c.analyzed ? (r.covered?.[c.id] ? 'Có' : 'Không') : 'Chưa phân tích')),
				r.covered_count, VERDICT_META[r.verdict]?.label || '',
			]),
		],
	}];

	const buildPdf = () => ({
		title: 'Độ phủ từ khoá so với đối thủ',
		subtitle: data?.range ? `Dữ liệu GSC ${data.range.from} → ${data.range.to}` : '',
		sections: [{
			columns: ['Từ khoá', 'Impressions', 'Vị trí', 'Đối thủ phủ', 'Đánh giá'],
			rows: (data?.rows || []).slice(0, 100).map((r) => [
				r.keyword, r.impressions, r.position || '—', r.covered_count, VERDICT_META[r.verdict]?.label || '—',
			]),
		}],
	});

	return (
		<div>
			{!anyAnalyzed && !isFetching && (
				<Alert type="warning" showIcon style={{marginBottom: 14}}
					message="Chưa có trang đối thủ nào được phân tích"
					description='Ma trận cần cụm từ của trang đối thủ. Sang tab "Content Gap" phân tích trang của từng đối thủ trước — cột đối thủ chưa phân tích hiển thị "?".' />
			)}

			<Alert type="info" showIcon style={{marginBottom: 14}}
				message="Độ phủ tính trên các trang đối thủ ĐÃ phân tích (không phải toàn site đối thủ) — kết quả là heuristic tham khảo, không phải thứ hạng thật của đối thủ." />

			<div className={cn('actionBar')}>
				<Segmented
					value={verdictFilter}
					onChange={setVerdictFilter}
					options={[
						{value: 'all', label: 'Tất cả'},
						{value: 'defend', label: 'Cần phòng thủ'},
						{value: 'advantage', label: 'Đang lợi thế'},
					]}
				/>
				<ExportButtons filename={`do-phu-tu-khoa-${projectId}`} buildExcel={buildExcel} buildPdf={buildPdf} />
			</div>

			<Table
				rowKey="keyword"
				size="small"
				loading={isFetching}
				columns={columns}
				dataSource={rows}
				scroll={{x: 'max-content'}}
				locale={{emptyText: 'Không có từ khoá nào (theo bộ lọc hiện tại).'}}
				pagination={{pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}}
			/>
		</div>
	);
}

export default CompetitorCoverage;
